import React, { useEffect, useState } from 'react';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { NamedLink } from '../../components';

import css from './CookieConsent.module.css';

const STORAGE_KEY = 'tphCookieConsent';

// Dormant gate: the banner only renders when this build-time flag is explicitly
// set to 'true'. Unset/false in every environment by default, so it stays hidden
// for real users until the analytics/consent stack is wired up.
const isGateEnabled = process.env.REACT_APP_COOKIE_CONSENT_ENABLED === 'true';

const readStoredConsent = () => {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch (e) {
    return null;
  }
};

const writeStoredConsent = value => {
  try {
    window.localStorage.setItem(STORAGE_KEY, value);
  } catch (e) {
    // Ignore write failures (e.g. private mode / disabled storage).
  }
};

const isPreviewRequested = () => {
  if (typeof window === 'undefined') {
    return false;
  }
  try {
    return new URLSearchParams(window.location.search).get('cookiePreview') === '1';
  } catch (e) {
    return false;
  }
};

/**
 * Cookie consent banner (v1 — notice only, no script gating).
 *
 * SSR-safe: renders null on the server and on the first client render; the
 * visibility decision is made inside a mount effect once `window` is available.
 *
 * Visibility rules:
 *  - `?cookiePreview=1` forces the banner to render (for staging review),
 *    regardless of the gate or stored consent.
 *  - Otherwise it renders only when the dormant gate is on AND no choice is
 *    stored in localStorage.
 *
 * @returns {JSX.Element|null}
 */
const CookieConsent = () => {
  const intl = useIntl();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (isPreviewRequested()) {
      setIsVisible(true);
      return;
    }
    if (isGateEnabled && !readStoredConsent()) {
      setIsVisible(true);
    }
  }, []);

  if (!isVisible) {
    return null;
  }

  const handleChoice = value => {
    writeStoredConsent(value);
    setIsVisible(false);
  };

  return (
    <div
      className={css.root}
      role="region"
      aria-label={intl.formatMessage({ id: 'CookieConsent.ariaLabel' })}
    >
      <div className={css.bar}>
        <p className={css.message}>
          <FormattedMessage id="CookieConsent.message" />
        </p>
        <div className={css.actions}>
          <NamedLink name="PrivacyPolicyPage" className={css.preferences}>
            <FormattedMessage id="CookieConsent.preferences" />
          </NamedLink>
          <button
            type="button"
            className={css.reject}
            onClick={() => handleChoice('essential')}
          >
            <FormattedMessage id="CookieConsent.reject" />
          </button>
          <button
            type="button"
            className={css.acceptAll}
            onClick={() => handleChoice('all')}
          >
            <FormattedMessage id="CookieConsent.acceptAll" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
